import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchBooksInfinite } from '@/features/library/hooks/useSearchBooksInfinite';
import type { Book } from '@/features/library/api/books';

type CarouselBook = Pick<Book, 'book_id' | 'title' | 'author' | 'cover_image_url' | 'topic'>;

interface BookDetailCoverCarouselProps {
  currentBook: CarouselBook;
  booksFromContext?: CarouselBook[];
  initialIndex?: number;
  source?: string;
}

const toBook = (book: CarouselBook): Book => ({
  ...book,
  cover_image_url: book.cover_image_url ?? null,
  description: '',
  price: '',
  topic: book.topic || '',
  created_at: '',
});

const BookDetailCoverCarousel: React.FC<BookDetailCoverCarouselProps> = ({ currentBook, booksFromContext, initialIndex = 0, source }) => {
  const navigate = useNavigate();
  const trackRef = useRef<HTMLDivElement | null>(null);
  const currentBookIdRef = useRef(currentBook.book_id);
  const booksRef = useRef<Book[]>([]);
  const settleTimerRef = useRef<number | null>(null);
  const alignmentTimerRef = useRef<number | null>(null);
  const alignmentTokenRef = useRef(0);
  const programmaticAlignmentRef = useRef(false);
  const hasAlignedCurrentRef = useRef<number | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const pointerMovedRef = useRef(false);
  const [activeBookId, setActiveBookId] = useState(currentBook.book_id);

  const hasContextBooks = Boolean(booksFromContext?.length);
  const { books: fetchedBooks, isFetchingNextPage, hasNextPage, fetchNextPage } = useSearchBooksInfinite(
    currentBook.topic ? { topic: currentBook.topic } : {},
    { enabled: !hasContextBooks && Boolean(currentBook.topic), limit: 10 },
  );

  // The source page owns ordering. API results are only a fallback for a
  // detail page opened directly, so pagination cannot reorder the carousel.
  const books = useMemo(() => {
    const sourceBooks = hasContextBooks ? booksFromContext ?? [] : fetchedBooks;
    const result: Book[] = [];
    const seen = new Set<number>();

    sourceBooks.forEach((book) => {
      if (!seen.has(book.book_id)) {
        seen.add(book.book_id);
        result.push(toBook(book));
      }
    });

    if (!seen.has(currentBook.book_id)) {
      const insertAt = Math.max(0, Math.min(initialIndex, result.length));
      result.splice(insertAt, 0, toBook(currentBook));
    } else {
      const index = result.findIndex((book) => book.book_id === currentBook.book_id);
      result[index] = toBook(currentBook);
    }

    return result;
  }, [booksFromContext, currentBook, fetchedBooks, hasContextBooks, initialIndex]);

  booksRef.current = books;
  currentBookIdRef.current = currentBook.book_id;

  const findClosestBookId = (container: HTMLDivElement) => {
    const center = container.getBoundingClientRect().left + container.clientWidth / 2;
    let closestId: number | undefined;
    let closestDistance = Infinity;
    Array.from(container.children).forEach((element) => {
      const node = element as HTMLElement;
      const rect = node.getBoundingClientRect();
      const distance = Math.abs(center - (rect.left + rect.width / 2));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestId = Number(node.dataset.bookId);
      }
    });
    return closestId;
  };

  const setActive = (bookId: number) => setActiveBookId((previous) => previous === bookId ? previous : bookId);

  const navigateToBook = (bookId: number) => {
    if (bookId === currentBookIdRef.current) return;
    navigate('/library/book', {
      state: {
        bookId,
        source,
        books: booksRef.current.map(({ book_id, title, author, cover_image_url, topic }) => ({ book_id, title, author, cover_image_url, topic })),
        initialIndex: booksRef.current.findIndex((book) => book.book_id === bookId),
      },
    });
  };

  const settleUserScroll = () => {
    const container = trackRef.current;
    if (!container || programmaticAlignmentRef.current) return;
    const closestId = findClosestBookId(container);
    if (closestId === undefined) return;
    setActive(closestId);
    navigateToBook(closestId);
  };

  const scheduleUserScrollSettle = () => {
    if (programmaticAlignmentRef.current) return;
    // Modern browsers emit scrollend; use the timer only for older mobile
    // WebViews that do not implement that event.
    const container = trackRef.current;
    if (container && 'onscrollend' in container) return;
    if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
    settleTimerRef.current = window.setTimeout(() => {
      settleTimerRef.current = null;
      settleUserScroll();
    }, 80);
  };

  const centerBookAndNavigate = (bookId: number) => {
    if (bookId === currentBookIdRef.current) return;
    const container = trackRef.current;
    const child = container && Array.from(container.children).find(
      (element) => Number((element as HTMLElement).dataset.bookId) === bookId,
    ) as HTMLElement | undefined;
    if (!container || !child) return;

    if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
    if (alignmentTimerRef.current !== null) window.clearTimeout(alignmentTimerRef.current);

    const token = ++alignmentTokenRef.current;
    programmaticAlignmentRef.current = true;
    child.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

    const finish = () => {
      if (token !== alignmentTokenRef.current) return;
      programmaticAlignmentRef.current = false;
      if (alignmentTimerRef.current !== null) window.clearTimeout(alignmentTimerRef.current);
      alignmentTimerRef.current = null;
      setActive(bookId);
      navigateToBook(bookId);
    };

    container.addEventListener('scrollend', finish, { once: true });
    alignmentTimerRef.current = window.setTimeout(finish, 350);
  };

  // Align only when the selected book changes. Loading another page must not
  // restart a smooth scroll while the user is dragging.
  useLayoutEffect(() => {
    const container = trackRef.current;
    if (!container || books.length === 0 || hasAlignedCurrentRef.current === currentBook.book_id) return;
    const child = Array.from(container.children).find((element) => Number((element as HTMLElement).dataset.bookId) === currentBook.book_id) as HTMLElement | undefined;
    if (!child) return;

    hasAlignedCurrentRef.current = currentBook.book_id;
    const token = ++alignmentTokenRef.current;
    programmaticAlignmentRef.current = true;
    setActive(currentBook.book_id);
    child.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

    const finish = () => {
      if (token !== alignmentTokenRef.current) return;
      programmaticAlignmentRef.current = false;
      if (alignmentTimerRef.current !== null) {
        window.clearTimeout(alignmentTimerRef.current);
        alignmentTimerRef.current = null;
      }
      setActive(currentBook.book_id);
    };
    container.addEventListener('scrollend', finish, { once: true });
    alignmentTimerRef.current = window.setTimeout(finish, 450);
    return () => {
      container.removeEventListener('scrollend', finish);
      if (alignmentTimerRef.current !== null) window.clearTimeout(alignmentTimerRef.current);
    };
  }, [books, currentBook.book_id]);

  useEffect(() => {
    const container = trackRef.current;
    if (!container) return;
    const handleScroll = () => scheduleUserScrollSettle();
    const handleScrollEnd = () => settleUserScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('scrollend', handleScrollEnd, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('scrollend', handleScrollEnd);
      if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
    };
  });

  useEffect(() => {
    const container = trackRef.current;
    if (!container) return;
    const loadMore = () => {
      if (isFetchingNextPage || !hasNextPage) return;
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (maxScroll <= 0) return;
      const position = Math.abs(container.scrollLeft);
      const distanceFromEdge = Math.min(position, Math.abs(maxScroll - position));
      if (distanceFromEdge <= container.clientWidth * 0.8) void fetchNextPage();
    };
    container.addEventListener('scroll', loadMore, { passive: true });
    return () => container.removeEventListener('scroll', loadMore);
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => () => {
    if (alignmentTimerRef.current !== null) window.clearTimeout(alignmentTimerRef.current);
    if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
  }, []);

  const handleUserInteractionStart = () => {
    // A real touch/drag always wins over the initial programmatic alignment.
    // This is important on mobile where the first swipe can happen before
    // scrollend is supported or before the alignment fallback timer expires.
    if (programmaticAlignmentRef.current) {
      programmaticAlignmentRef.current = false;
      alignmentTokenRef.current += 1;
      if (alignmentTimerRef.current !== null) {
        window.clearTimeout(alignmentTimerRef.current);
        alignmentTimerRef.current = null;
      }
    }
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
    pointerMovedRef.current = false;
    handleUserInteractionStart();
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current;
    if (!start) return;
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) {
      pointerMovedRef.current = true;
    }
  };

  const handleCoverClick = (bookId: number) => {
    const wasDragged = pointerMovedRef.current;
    pointerStartRef.current = null;
    pointerMovedRef.current = false;
    if (wasDragged || bookId === currentBookIdRef.current) return;
    centerBookAndNavigate(bookId);
  };

  if (books.length <= 1) {
    const book = books[0] ?? toBook(currentBook);
    return <div className="flex justify-center mt-0 mb-1 bg-white"><div className="relative w-48 md:w-52 aspect-[0.66] rounded-xl overflow-hidden shadow-[0_0_6px_1px_rgba(0,0,0,0.16)]">{book.cover_image_url ? <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" decoding="async" /> : <div className="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500" />}</div></div>;
  }

  return (
    <div className="flex justify-center mt-0 mb-4 bg-white">
      <div
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onTouchStart={handleUserInteractionStart}
        className="-my-4 flex overflow-x-auto overflow-y-hidden gap-4 bg-white px-6 py-4 hide-scrollbar snap-x snap-mandatory touch-pan-x"
        style={{ touchAction: 'pan-x', overscrollBehaviorX: 'contain', overscrollBehaviorY: 'none' }}
      >
        {books.map((book, index) => (
          <div key={book.book_id} data-book-id={book.book_id} className="snap-center flex-shrink-0 w-48 md:w-52 my-3">
            <div
              onClick={() => handleCoverClick(book.book_id)}
              className={`relative w-48 md:w-52 transition-all duration-200 cursor-pointer ${book.book_id === activeBookId ? 'scale-[1.04] opacity-100' : 'scale-90 opacity-75'}`}
            >
              <div className="relative aspect-[0.66] rounded-xl overflow-hidden shadow-[0_0_8px_1px_rgba(0,0,0,0.16)]">
                {book.cover_image_url ? <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" loading={index < 2 ? 'eager' : 'lazy'} decoding="async" /> : <div className="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500" />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(BookDetailCoverCarousel);
